import React, { useEffect, useRef, useState, useMemo } from 'react';
import { embedDashboard } from "@superset-ui/embedded-sdk";
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { Lightbulb } from '@mui/icons-material';
import InsightsModal from '../Common/InsightsModal';
import axios from 'axios';
import { BarChart } from '@mui/x-charts/BarChart';
import dayjs from 'dayjs';
import {
    Box,
    Typography,
    IconButton,
    Stack
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import moment from 'moment';
import { useParams } from 'react-router-dom';

const COLORS = [
    '#1976d2', '#dc004e', '#388e3c', '#f57c00',
    '#7b1fa2', '#0097a7', '#c2185b', '#0288d1',
    '#512da8', '#afb42b', '#5d4037', '#00796b',
    '#e64a19', '#689f38', '#fbc02d', '#303f9f'
];



const SustainabilityGraph = ({ dashboardHeight, dashboardId, projects, facilities, responseData, chartTitle, maxRange, scaleType }) => {


    const pageSize = 4;
    const { client_id } = useParams();

    const [openModal, setOpenModal] = useState(false);
    const [insightKey, setInsightKey] = useState(null)
    const [jsonData, setJsonData] = useState(null)
    const [showInsightButton, setShowInsightButton] = useState(true);

    const [legendPage, setLegendPage] = useState(0);
    const [chartData, setChartData] = useState({ series: [], dataset: [] })
    const [facilityLabels, setFaceilityLabels] = useState([]);


    useEffect(() => {
        const all = new Set();
        responseData.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'month') {
                    all.add(key);
                }
            });
        });
        const facilityLabels = Array.from(all);
        setFaceilityLabels(facilityLabels);
        const series = facilityLabels.map((label, i) => ({
            dataKey: label,
            label,
            color: COLORS[i % COLORS.length],
        }));

        const dataset = responseData.map((item) => {
            const date = new Date(item.month);
            const row = { month: date };
            facilityLabels.forEach(label => {
                row[label] = item[label] ? item[label] : 0;
            });

            return row;
        });
        setChartData({ series, dataset })


    }, [responseData]);



    const generateInsightKey = (...args) => {
        const key = args.join('-')
        return `g-${key}`
    }

    const prepareInsightModal = (title, responseData) => {
        if (openModal) return;

        setOpenModal(true);
        setShowInsightButton(false);
        setJsonData({
            chartTitle: title,
            localChartData: responseData
        });
    };

    const totalPages = Math.ceil(facilityLabels.length / pageSize);
    const paginatedSeries = chartData.series.slice(legendPage * pageSize, legendPage * pageSize + pageSize);


    return (
        <div id={`superset-db-${dashboardId}`}>
            <div style={{ height: dashboardHeight, padding: 16, backgroundColor: 'white', marginBottom: '24px' }}>
                {(chartTitle) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        {chartTitle && <h6 style={{ fontWeight: 'bold', margin: 0 }}>{chartTitle}</h6>}
                        <Tooltip title="view-insights">
                            <Button
                                id="summary-insight"
                                style={{ border: 'none' }}
                                size="small"
                                startIcon={<Lightbulb color="info" />}
                                variant="outlined"
                                onClick={() => {
                                    prepareInsightModal(chartTitle, responseData);
                                    const key = generateInsightKey(
                                        client_id,
                                        `facilities-${facilities.map(fItem => fItem).join('-')}`,
                                        `pids-${projects.map(pItem => pItem).join('-')}`,
                                        dashboardId
                                    );
                                    setInsightKey(key);
                                }}
                            >
                                View Insights
                            </Button>
                        </Tooltip>
                    </div>
                )}   <Box>
                    <Box mt={2}>
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent='end'>
                            {paginatedSeries.map((s, idx) => (
                                <Box key={s.dataKey} display="flex" alignItems="center" mr={2}>
                                    <Box
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: '4px',
                                            backgroundColor: s.color,
                                            mr: 1,
                                        }}
                                    />
                                    <Typography variant="body2">{s.label}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} gap={1}>
                            <IconButton
                                size="small"
                                disabled={legendPage === 0}
                                onClick={() => setLegendPage(p => p - 1)}
                            >
                                <ArrowBackIosIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2">{legendPage + 1}/{totalPages}</Typography>
                            <IconButton
                                size="small"
                                disabled={legendPage >= totalPages - 1}
                                onClick={() => setLegendPage(p => p + 1)}
                            >
                                <ArrowForwardIosIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                    <BarChart
                        dataset={chartData.dataset}
                        xAxis={[{
                            dataKey: 'month',
                            scaleType: 'band',
                            valueFormatter: (item) => moment(item).format('YYYY-MM')
                        }]}
                        series={chartData.series}
                        height={360}
                        margin={{ top: 40, bottom: 40, left: 80, right: 40 }}
                        yAxis={[{ width: 90, max: maxRange, scaleType: `${scaleType}` }]}
                        slotProps={{ legend: { hidden: true } }}

                    />
                </Box>
            </div>

            <InsightsModal
                convertedJson={jsonData}
                openInsight={openModal}
                closeInsight={() => setOpenModal(false)}
                insightKey={insightKey}
                setShowInsightButton={() => setShowInsightButton(true)}
            />
        </div>
    );
};

export default SustainabilityGraph;
